import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/marketing/components/ui/button';
import { Checkbox } from '@/marketing/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/marketing/components/ui/popover';

interface MultiSelectFilterProps {
  label: string;
  options: readonly { value: string; label: ReactNode }[];
  selected: string[];
  onSelectionChange: (values: string[]) => void;
}

export function MultiSelectFilter({ label, options, selected, onSelectionChange }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter((v) => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  };

  const displayLabel = selected.length > 0 ? `${label}: ${selected.length}` : label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="tw-h-10 tw-gap-1 tw-font-normal">
          {displayLabel}
          <ChevronDown className="tw-h-4 tw-w-4 tw-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw-w-52 tw-p-2" align="start">
        <div className="tw-max-h-60 tw-overflow-auto">
          {options.map((option) => (
            <label
              key={option.value}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-px-2 tw-py-1.5 tw-text-sm hover:tw-bg-accent tw-cursor-pointer"
            >
              <Checkbox checked={selected.includes(option.value)} onCheckedChange={() => handleToggle(option.value)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="tw-border-t tw-pt-2 tw-mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="tw-w-full tw-h-8 tw-text-xs"
              onClick={() => onSelectionChange([])}
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
